import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import useGameBase from '../../hooks/useGameBase';
import { useSound } from '../../utils/sound/SoundProvider';
import SpectatorPerspectiveSelector from './SpectatorPerspectiveSelector';

/**
 * CrosswordGame Component
 * 
 * Non-turn-based multiplayer crossword puzzle game.
 * Players race to complete the crossword first.
 */
export default function CrosswordGame({ roomId, gameState = {}, gamePlayers = [], user = {}, socket, isSpectator = false }) {
  // --- Spectator Perspective logic (modular, opt-in) ---
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { submitAction, perspectiveUser, perspectiveIds } = useGameBase({
    gameState,
    gamePlayers,
    user,
    socket,
    roomId,
    perspectivePlayer,
  });
  const sound = useSound();
  const perspectiveSocketId = perspectiveUser?.socketId || null;

  const [selectedClue, setSelectedClue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const puzzle = gameState.puzzle || {};
  const grid = puzzle.grid || [];
  const clues = useMemo(() => puzzle.clues || { across: [], down: [] }, [puzzle.clues]);
  const playerProgress = gameState.playerProgress || {};
  const findPerspectiveValue = useCallback((source) => {
    if (!source || typeof source !== 'object') return null;
    for (const key of perspectiveIds || []) {
      if (typeof key !== 'undefined' && key !== null && source[key]) {
        return source[key];
      }
    }
    return null;
  }, [perspectiveIds]);
  const myProgress = findPerspectiveValue(playerProgress) || { completedWords: [], grid: [] };
  const myGrid = myProgress.grid || [];
  const giveUpRequest = gameState.giveUpRequest || null;
  const showSolution = !!gameState.revealSolution;
  const myApprovedGiveUp = !!(perspectiveSocketId && giveUpRequest?.approvals?.[perspectiveSocketId]);
  const requestedByMe = giveUpRequest?.requestedBy === perspectiveSocketId;
  const requesterName = gamePlayers.find((player) => player.socketId === giveUpRequest?.requestedBy)?.username || 'A player';
  const winnerBreakdown = Array.isArray(gameState.result?.breakdown)
    ? gameState.result.breakdown.find((entry) => entry.socketId === gameState.winner)
    : null;

  // Get clues by direction
  const acrossClues = useMemo(() => 
    (clues.across || []).sort((a, b) => a.number - b.number),
    [clues]
  );
  
  const downClues = useMemo(() => 
    (clues.down || []).sort((a, b) => a.number - b.number),
    [clues]
  );

  // Get cells that belong to the selected clue
  const selectedCells = useMemo(() => {
    if (!selectedClue) return new Set();
    const cells = new Set();
    const { row, col, direction, answer } = selectedClue;
    for (let i = 0; i < answer.length; i++) {
      const r = direction === 'across' ? row : row + i;
      const c = direction === 'across' ? col + i : col;
      cells.add(`${r}-${c}`);
    }
    return cells;
  }, [selectedClue]);

  // Get numbers for each cell
  // Calculate cell numbers from all clues
  const cellNumbers = useMemo(() => {
    const numbers = {};
    const allClues = [...(clues.across || []), ...(clues.down || [])];
    allClues.forEach(clue => {
      const key = `${clue.row}-${clue.col}`;
      if (!numbers[key] || numbers[key] > clue.number) {
        numbers[key] = clue.number;
      }
    });
    return numbers;
  }, [clues]);

  // Focus input when clue is selected
  useEffect(() => {
    if (selectedClue && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedClue]);

  useEffect(() => {
    if (gameState.finished) {
      setSelectedClue(null);
      setInputValue('');
    }
  }, [gameState.finished]);

  // Handle cell click
  const handleCellClick = (row, col) => {
    if (gameState.finished) return;
    // Find clues that start at or pass through this cell
    const allClues = [...(clues.across || []), ...(clues.down || [])];
    const matchingClues = allClues.filter(clue => {
      const { row: startRow, col: startCol, direction, answer } = clue;
      if (direction === 'across') {
        return row === startRow && col >= startCol && col < startCol + answer.length;
      } else {
        return col === startCol && row >= startRow && row < startRow + answer.length;
      }
    });

    if (matchingClues.length > 0) {
      // If already selected and there are multiple clues, cycle to next one
      if (selectedClue) {
        const currentIndex = matchingClues.findIndex(c => c.id === selectedClue.id);
        const nextIndex = (currentIndex + 1) % matchingClues.length;
        setSelectedClue(matchingClues[nextIndex]);
      } else {
        setSelectedClue(matchingClues[0]);
      }
      setInputValue('');
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
    setInputValue(value);

    if (selectedClue && value.length > 0) {
      // Submit when we've typed enough letters
      if (value.length >= selectedClue.answer.length) {
        handleSubmit(value);
      }
    }
  };

  // Handle submit
  const handleSubmit = async (wordToSubmit) => {
    if (!selectedClue || gameState.finished) return;

    const word = wordToSubmit || inputValue;
    const letters = word.split('');

    const result = await submitAction({
      type: 'enter_word',
      clueId: selectedClue.id,
      letters,
    });

    if (result && result.correct === true) {
      try { sound && sound.play && sound.play('ui:positive'); } catch (e) {}
      setInputValue('');
      setSelectedClue(null);
    } else if (result && result.correct === false) {
      try { sound && sound.play && sound.play('ui:negative'); } catch (e) {}
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && inputValue.length > 0) {
      handleSubmit();
    } else if (e.key === 'Escape') {
      setSelectedClue(null);
      setInputValue('');
    }
  };

  // Check if a clue is completed
  const isClueCompleted = (clue) => {
    return myProgress.completedWords && myProgress.completedWords.includes(clue.id);
  };

  const handleGiveUpRequest = async () => {
    const result = await submitAction({ type: 'request_give_up' });
    if (result?.requested) {
      try { sound && sound.play && sound.play('ui:open'); } catch (e) {}
    }
  };

  const handleGiveUpResponse = async (approve) => {
    const result = await submitAction({ type: 'respond_give_up', approve });
    if (approve && (result?.approved || result?.ended)) {
      try { sound && sound.play && sound.play('ui:positive'); } catch (e) {}
    }
    if (!approve && result?.declined) {
      try { sound && sound.play && sound.play('ui:negative'); } catch (e) {}
    }
  };

  // Get letter at position
  const getLetterAt = (row, col) => {
    if (showSolution) {
      return grid[row]?.[col] || '';
    }
    if (myGrid[row] && myGrid[row][col]) {
      return myGrid[row][col];
    }
    return '';
  };

  // Calculate completion percentage for progress bars
  const getPlayerCompletion = (socketId) => {
    const progress = playerProgress[socketId];
    if (!progress) return 0;
    const totalClues = (clues.across?.length || 0) + (clues.down?.length || 0);
    if (totalClues === 0) return 0;
    return Math.round((progress.completedWords.length / totalClues) * 100);
  };

  const getPlayerCompletionTime = (socketId) => {
    const progress = playerProgress[socketId];
    return progress?.completionTimeDisplay || null;
  };

  if (!gameState.started) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600 dark:text-gray-400">Waiting for game to start...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 select-none">
      {/* Spectator Perspective Selector */}
      {isSpectator && perspectiveOptions.length > 0 && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId}
          onChange={setSpectatorViewPlayerId}
        />
      )}

      {/* Player Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Player Progress
        </h3>
        <div className="space-y-2">
          {gamePlayers.map(player => (
            <div key={player.socketId} className="flex items-center gap-2">
              <div className="min-w-0">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {player.username}
                </div>
                {getPlayerCompletionTime(player.socketId) && (
                  <div className="text-xs text-emerald-600 dark:text-emerald-400">
                    Finished in {getPlayerCompletionTime(player.socketId)}
                  </div>
                )}
              </div>
              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-blue-500 dark:bg-blue-400 h-full transition-all duration-300"
                  style={{ width: `${getPlayerCompletion(player.socketId)}%` }}
                />
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {getPlayerCompletion(player.socketId)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-4 select-none">
        {/* Crossword Grid */}
        <div className="flex justify-center bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg select-none">
          <div
            className="inline-block bg-[#111] dark:bg-[#111] p-1 rounded-lg shadow-lg select-none"
          >
            <table
              className="border-collapse"
              style={{
                margin: 0,
                tableLayout: 'fixed',
                width: grid[0] ? `${grid[0].length * 28}px` : undefined,
                height: grid.length ? `${grid.length * 28}px` : undefined,
              }}
            >
              <tbody>
                {grid.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, colIndex) => {
                      const key = `${rowIndex}-${colIndex}`;
                      const cellNumber = cellNumbers[key];
                      const isSelected = selectedCells.has(key);
                      const letter = getLetterAt(rowIndex, colIndex);
                      if (cell === null) {
                        // Black cell: no border, perfect square
                        return (
                          <td
                            key={key}
                            style={{
                              width: '28px',
                              height: '28px',
                              aspectRatio: '1 / 1',
                              background: '#111',
                              padding: 0,
                            }}
                          />
                        );
                      }
                      // Word cell: border on all sides, perfect square
                      const isTopEdge = rowIndex === 0;
                      const isBottomEdge = rowIndex === grid.length - 1;
                      const isLeftEdge = colIndex === 0;
                      const isRightEdge = colIndex === row.length - 1;
                      const aboveCell = !isTopEdge ? grid[rowIndex - 1][colIndex] : null;
                      const belowCell = !isBottomEdge ? grid[rowIndex + 1][colIndex] : null;
                      const leftCell = !isLeftEdge ? row[colIndex - 1] : null;
                      const rightCell = !isRightEdge ? row[colIndex + 1] : null;
                      const borderTop = isTopEdge || aboveCell === null ? '1px solid #aaa' : '1px solid #bbb';
                      const borderBottom = isBottomEdge || belowCell === null ? '1px solid #aaa' : '1px solid #bbb';
                      const borderLeft = isLeftEdge || leftCell === null ? '1px solid #aaa' : '1px solid #bbb';
                      const borderRight = isRightEdge || rightCell === null ? '1px solid #aaa' : '1px solid #bbb';
                      return (
                        <td
                          key={key}
                          style={{
                            width: '28px',
                            height: '28px',
                            aspectRatio: '1 / 1',
                            padding: 0,
                            borderTop,
                            borderBottom,
                            borderLeft,
                            borderRight,
                          }}
                          className={
                            `relative p-0 ` +
                            (isSelected
                              ? 'bg-blue-100 dark:bg-blue-500/50 text-gray-900 dark:text-gray-100'
                              : 'bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700') +
                            (letter ? ' text-gray-900 dark:text-gray-100' : ' text-gray-400 dark:text-gray-600')
                          }
                        >
                          <button
                            onClick={() => handleCellClick(rowIndex, colIndex)}
                            disabled={gameState.finished}
                            style={{ width: '100%', height: '100%', aspectRatio: '1 / 1', padding: 0, border: 'none', background: 'transparent', position: 'relative', display: 'block' }}
                          >
                            {typeof cellNumber !== 'undefined' && cellNumber !== null && (
                              <span
                                className="absolute text-[9px] font-semibold leading-none"
                                style={{
                                  top: '2px',
                                  left: '2px',
                                  color: 'black',
                                  zIndex: 2,
                                }}
                              >
                                <span className="dark:hidden">{cellNumber}</span>
                                <span className="hidden dark:inline" style={{ color: 'white' }}>{cellNumber}</span>
                              </span>
                            )}
                            <span className="mt-1">{letter}</span>
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>



        {/* Clues Panel */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow space-y-4">
          {/* Selected Clue Input */}
          {selectedClue && !isClueCompleted(selectedClue) && !gameState.finished && (
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 border-2 border-blue-500">
              <div className="mb-2">
                <span className="font-bold text-blue-700 dark:text-blue-300">
                  {selectedClue.number} {selectedClue.direction === 'across' ? 'Across' : 'Down'}
                </span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                {selectedClue.clue}
              </p>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                placeholder={`Type answer (${selectedClue.answer.length} letters)`}
                maxLength={selectedClue.answer.length}
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => handleSubmit()}
                  disabled={inputValue.length === 0}
                  className="flex-1 px-3 py-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white rounded text-sm"
                >
                  Submit
                </button>
                <button
                  onClick={() => {
                    setSelectedClue(null);
                    setInputValue('');
                  }}
                  className="px-3 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Clues in two columns */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Across Clues */}
            <div>
              <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">Across</h3>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {acrossClues.map(clue => (
                <button
                  key={clue.id}
                  onClick={() => {
                    if (!isClueCompleted(clue)) {
                      setSelectedClue(clue);
                      setInputValue('');
                    }
                  }}
                  className={`
                    w-full text-left p-2 rounded text-sm
                    ${isClueCompleted(clue)
                      ? 'bg-green-100 dark:bg-green-900/30 text-gray-500 dark:text-gray-400 line-through cursor-default'
                      : selectedClue?.id === clue.id
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-gray-900 dark:text-gray-100'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }
                  `}
                  disabled={isClueCompleted(clue) || gameState.finished}
                >
                  <span className="font-semibold">{clue.number}.</span> {clue.clue} <span className="text-gray-500 dark:text-gray-400">({clue.answer.length})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Down Clues */}
          <div>
            <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">Down</h3>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {downClues.map(clue => (
                <button
                  key={clue.id}
                  onClick={() => {
                    if (!isClueCompleted(clue)) {
                      setSelectedClue(clue);
                      setInputValue('');
                    }
                  }}
                  className={`
                    w-full text-left p-2 rounded text-sm
                    ${isClueCompleted(clue)
                      ? 'bg-green-100 dark:bg-green-900/30 text-gray-500 dark:text-gray-400 line-through cursor-default'
                      : selectedClue?.id === clue.id
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-gray-900 dark:text-gray-100'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }
                  `}
                  disabled={isClueCompleted(clue) || gameState.finished}
                >
                  <span className="font-semibold">{clue.number}.</span> {clue.clue} <span className="text-gray-500 dark:text-gray-400">({clue.answer.length})</span>
                </button>
              ))}
            </div>
          </div>

          
          </div>
          {/* Header with puzzle title */}
      <div className="text-center">

        {gameState.finished && (
          <p className="text-green-600 dark:text-green-400 font-semibold mt-2">
            {gameState.endedByConsensus
              ? `Game ended early. ${gameState.result?.winnerNames ? `${gameState.result.winnerNames} ${gameState.result.winners?.length > 1 ? 'lead' : 'leads'}.` : 'Final standings are shown below.'}`
              : `🎉 ${gamePlayers.find(p => p.socketId === gameState.winner)?.username || 'Someone'} won${winnerBreakdown?.completionTimeDisplay ? ` in ${winnerBreakdown.completionTimeDisplay}` : ''}!`}
          </p>
        )}
        {!gameState.finished && (
          <div className="mt-3 flex items-center justify-center gap-2">
            <button
              onClick={handleGiveUpRequest}
              disabled={!!giveUpRequest}
              className="rounded-md bg-rose-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:bg-gray-400 dark:disabled:bg-gray-700"
            >
              Give up
            </button>
          </div>
        )}
        {giveUpRequest && !gameState.finished && (
          <div className="mx-auto mt-4 max-w-lg rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-100">
            <div className="font-semibold">
              {requestedByMe ? 'Waiting for the other players to respond to your give up request.' : `${requesterName} wants to end the game early.`}
            </div>
            <div className="mt-1">
              If every player agrees, the game will end now and the full crossword will be revealed.
            </div>
            <div className="mt-2 text-xs opacity-80">
              {Object.keys(giveUpRequest.approvals || {}).length} / {gamePlayers.length} players have agreed.
            </div>
            {!requestedByMe && !myApprovedGiveUp && (
              <div className="mt-3 flex items-center justify-center gap-2">
                <button
                  onClick={() => handleGiveUpResponse(true)}
                  className="rounded-md bg-emerald-500 px-3 py-1.5 font-semibold text-white transition hover:bg-emerald-600"
                >
                  End game
                </button>
                <button
                  onClick={() => handleGiveUpResponse(false)}
                  className="rounded-md bg-gray-200 px-3 py-1.5 font-semibold text-gray-700 transition hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                >
                  Keep playing
                </button>
              </div>
            )}
          </div>
        )}
        {gameState.lastGiveUpMessage && !giveUpRequest && !gameState.finished && (
          <div className="mx-auto mt-3 max-w-lg rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-200">
            {gameState.lastGiveUpMessage}
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}

CrosswordGame.Guide = function CrosswordGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to play Crossword</h3>
      <p className="mb-1">A classic word puzzle game where players fill in a grid based on clues.</p>
      <p className="mb-1">The objective is to complete the crossword by correctly answering all the clues.</p>
      <p className="mb-1">Each clue corresponds to a word that fits into the grid either across or down.</p>
      <p className="mb-1">Players take turns or work simultaneously to fill in the grid.</p>
      <p className="mb-1">The game ends when all clues are correctly answered.</p>
      <p className="mb-1">Crossword is a game of knowledge, vocabulary, and problem-solving skills.</p>
      <p className="mb-1">Understanding the clues and thinking critically is key to success.</p>
    </div>
  );
};
